import React, { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { Printer, Plus, Trash2 } from "lucide-react";

export default function BarcodeGenerator() {
  const [codes, setCodes] = useState([]);
  const [input, setInput] = useState("");
  const [bulk, setBulk] = useState("");

  const addCode = () => {
    const v = input.trim();
    if (!v) return;
    setCodes([...codes, v]);
    setInput("");
  };

  const addBulk = () => {
    const list = bulk.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (list.length) setCodes([...codes, ...list]);
    setBulk("");
  };

  const remove = (i) => setCodes(codes.filter((_, idx) => idx !== i));
  const clear = () => setCodes([]);

  const printSheet = () => window.print();

  return (
    <div data-testid="barcode-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 no-print">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-2">── LABELS</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">BARCODE GENERATOR.</h1>
          <p className="text-sm text-[#666] mt-2 max-w-xl">Generate Code128 barcodes for design numbers. Print directly on label stock.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={clear} className="brut-btn brut-btn-secondary !py-2 text-xs" data-testid="clear-btn"><Trash2 size={14} strokeWidth={3} /> CLEAR</button>
          <button onClick={printSheet} className="brut-btn brut-btn-primary !py-2 text-xs" data-testid="print-btn"><Printer size={14} strokeWidth={3} /> PRINT</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6 no-print">
        <div className="brut-card p-5">
          <label className="label-mono">Add Single Design</label>
          <div className="flex gap-2">
            <input className="brut-input" value={input} onChange={(e) => setInput(e.target.value)}
                   placeholder="e.g. DSN-0451" data-testid="input-single" onKeyDown={(e) => e.key === "Enter" && addCode()} />
            <button onClick={addCode} className="brut-btn brut-btn-dark !py-2 text-xs" data-testid="add-single-btn">
              <Plus size={14} strokeWidth={3} />
            </button>
          </div>
        </div>
        <div className="brut-card p-5">
          <label className="label-mono">Bulk (one per line or comma)</label>
          <textarea className="brut-input min-h-[80px]" value={bulk} onChange={(e) => setBulk(e.target.value)}
                    placeholder="DSN-001&#10;DSN-002&#10;DSN-003" data-testid="input-bulk" />
          <button onClick={addBulk} className="brut-btn brut-btn-secondary !py-2 text-xs mt-2" data-testid="add-bulk-btn">
            <Plus size={14} strokeWidth={3} /> ADD BULK
          </button>
        </div>
      </div>

      {codes.length === 0 ? (
        <div className="brut-card p-10 text-center text-sm uppercase tracking-wider text-[#666]">No barcodes yet</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 print-grid">
          {codes.map((c, i) => (
            <BarcodeCard key={`${c}-${i}`} value={c} onRemove={() => remove(i)} testid={`barcode-${i}`} />
          ))}
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-grid { grid-template-columns: repeat(3, minmax(0,1fr)) !important; gap: 12px !important; }
        }
      `}</style>
    </div>
  );
}

function BarcodeCard({ value, onRemove, testid }) {
  const svgRef = useRef(null);
  useEffect(() => {
    if (svgRef.current) {
      try {
        JsBarcode(svgRef.current, value, {
          format: "CODE128", width: 2, height: 64, displayValue: true,
          fontSize: 14, font: "IBM Plex Mono", margin: 6, background: "#fff", lineColor: "#0A0A0A",
        });
      } catch (e) { /* noop */ }
    }
  }, [value]);
  return (
    <div className="brut-card p-3 flex flex-col items-center" data-testid={testid}>
      <svg ref={svgRef} className="w-full" />
      <button onClick={onRemove} className="text-[#E63946] mt-1 no-print" data-testid={`${testid}-remove`}>
        <Trash2 size={14} strokeWidth={3} />
      </button>
    </div>
  );
}
